import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider, useCart } from './contexts/CartContext';
import { Toaster } from './components/ui/sonner';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import PromoBanner from './components/PromoBanner';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Auth from './pages/Auth';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Admin from './pages/Admin';
import BulkOrder from './pages/BulkOrder';
import Quiz from './pages/Quiz';
import Compare from './pages/Compare';
import B2B from './pages/B2B';
import Partner from './pages/Partner';
import StoreLocator from './pages/StoreLocator';
import NotFound from './pages/NotFound';
import '@/App.css';

// Component to handle auth callback detection
function AppRouter({ cartCount }) {
  const location = useLocation();
  
  // Check URL fragment for session_id (Google OAuth callback)
  // This check happens synchronously during render to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <div className="App">
      <ScrollToTop />
      <PromoBanner />
      <Navbar cartCount={cartCount} />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/products" element={<Products />} />
        <Route path="/product/:id" element={<ProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/login" element={<Auth />} />
        <Route path="/register" element={<Auth />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/bulk-order" element={<BulkOrder />} />
        <Route path="/quiz" element={<Quiz />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/b2b" element={<B2B />} />
        <Route path="/partner" element={<Partner />} />
        <Route path="/stores" element={<StoreLocator />} />
        {/* M-05 FIX: 404 catch-all route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Footer />
      <WhatsAppButton />
      <Toaster 
        position="bottom-right"
        expand={true}
        richColors={true}
        closeButton={true}
        duration={4000}
        toastOptions={{
          style: {
            background: 'white',
            color: '#1F2937',
            padding: '16px 20px',
            borderRadius: '16px',
            fontSize: '14px',
            fontWeight: '500',
            maxWidth: '420px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            border: '1px solid #E5E7EB',
          },
          className: 'toast-custom',
          success: {
            style: {
              background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
              border: '1px solid #10B981',
              color: '#065F46',
            },
            icon: '✓',
          },
          error: {
            style: {
              background: 'linear-gradient(135deg, #FEF2F2 0%, #FECACA 100%)',
              border: '1px solid #EF4444',
              color: '#991B1B',
            },
            icon: '✕',
          },
          warning: {
            style: {
              background: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)',
              border: '1px solid #F59E0B',
              color: '#92400E',
            },
          },
          info: {
            style: {
              background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
              border: '1px solid #3B82F6',
              color: '#1E40AF',
            },
          },
        }}
      />
    </div>
  );
}

function AppContent() {
  const { getCartCount } = useCart();
  const cartCount = getCartCount();

  return (
    <BrowserRouter>
      <AppRouter cartCount={cartCount} />
    </BrowserRouter>
  );
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <AppContent />
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
